// Step 6 (Social Post route only) — final preview + 12-slot tweaks +
// download/save. Replaces what is normally Quality + Preview + Add Text
// for the AI-image routes.
//
// Layout: split panel.
//   LEFT:  scaled preview of the chosen designed post (PostEditorial today)
//   RIGHT: tweaks panel with 10 editable copy fields + Download / Save CTAs
//
// Cost model: $0 OpenAI image gen (the design IS the visual). Single
// /api/suggest-copy?format=social-post-12-slot call on mount to fill the
// 10 slots (~$0.001). Download produces a PNG via html-to-image — no
// upload, no credit consumption.

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toPng } from 'html-to-image';
import StepHeader from './StepHeader';
import PostEditorial from './social-designs/PostEditorial';
import PostBlush from './social-designs/PostBlush';
import PostBotanical from './social-designs/PostBotanical';
import PostMono from './social-designs/PostMono';
import PostNoir from './social-designs/PostNoir';
import PostPop from './social-designs/PostPop';
import PostVintage from './social-designs/PostVintage';
import PostStreet from './social-designs/PostStreet';
import JsonSocialPost from './social-designs/json-templates/JsonSocialPost';
import { getJsonTemplate } from './social-designs/json-templates/registry';
import type { SocialDesignId } from './StepSocialDesign';
import {
  suggestSocialCopy,
  generateImage,
  composeScene,
  InsufficientCreditsError,
  type SocialPostSlots,
} from '@/lib/api';
import {
  SOCIAL_DESIGN_BG_COLOR,
  SOCIAL_DESIGN_SLOT_ASPECT,
  SOCIAL_DESIGN_POST_BG_COLOR,
} from './social-designs/colors';
import { savePost } from '@/lib/gallery';

interface StepSocialFinalProps {
  productFile: File | null;
  productImageUrl: string;
  productName: string;
  productDescription: string;
  designId: SocialDesignId;
  /** 10-slot tweaks state lifted to the parent so it survives back-navigation
   *  to a different step and back. Parent owns the source of truth; this
   *  component reads + writes through the setter. */
  slots: SocialPostSlots | null;
  onSlotsChange: (s: SocialPostSlots) => void;
  /** Native export dimensions from the platform picked on Step 2. The
   *  design renders at 1080×1080 internally; pixelRatio = exportWidth /
   *  1080 scales the html-to-image output to the platform's resolution.
   *  Defaults to 1080×1080 (Instagram square) when the lookup fails. */
  exportWidth: number;
  exportHeight: number;
  onCostAdd: (delta: number) => void;
  /** Refresh the credit-pill in the header after a generation consumes
   *  credits. Same shape as StepPreview's prop. */
  onCreditsChanged?: () => void;
  /** From /api/analyze-product. When true, the backend compose call swaps
   *  to a no-shadow editorial prompt — cropped models (above/below waist
   *  shots) otherwise get a drop shadow drawn under their cut-off limbs,
   *  which reads as an amputation. Falsy / null defaults to product-shot
   *  prompt (with the soft ground shadow). */
  hasHumanModel?: boolean | null;
  /** Transparent-product PNG data URL produced by the new Confirm
   *  cutout step (via rembg). When set, the OpenAI compose call is
   *  skipped entirely — the design renders the transparent PNG
   *  directly on its slot bg color, with a CSS drop-shadow for the
   *  grounded look. When null/undefined, falls back to the prior
   *  OpenAI compose pipeline (kept as a safety net for routes that
   *  don't run the new step). */
  transparentProductImage?: string | null;
  /** Brand logo (from My Brand / /api/brand). Rendered into any
   *  `logo-slot` elements in JSON templates. */
  brandLogoUrl?: string | null;
  onBack: () => void;
  /** /appv2's Section wrapper supplies its own header; drop the inner
   *  StepHeader and lock root to a viewport-relative height so the
   *  internal split-panel flex math works in the flowing page. */
  compact?: boolean;
  /** Dev-only: when set, render a "Save as Recreate template (dev)" button
   *  next to Download. Captures the current design DOM (post-compose) via
   *  html-to-image and hands the base64 PNG up to the parent. Same prop
   *  as StepText. Currently wired from /appv2 only. */
  onSaveAsRecreate?: (previewPngBase64: string) => Promise<void> | void;
}

// Placeholder while the AI is filling. Lets the design render immediately
// (not as a blank rectangle) — replaced field-by-field as the suggest-copy
// call resolves. Also serves as the default-merge base so the 5 optional
// brand/scaffolding fields always render with sensible values — the
// suggest-copy endpoint only fills the 10 fields above the divider.
/** Decode a data URL into a File (sync). Used to feed the cropped
 *  product (from Step 2, a data URL) into the OpenAI compose calls,
 *  which take a File. */
function dataUrlToFile(dataUrl: string, name: string): File {
  const comma = dataUrl.indexOf(',');
  const meta = dataUrl.slice(0, comma);
  const b64 = dataUrl.slice(comma + 1);
  const mime = meta.match(/data:(.*?);/)?.[1] ?? 'image/png';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], name, { type: mime });
}

const PLACEHOLDER_SLOTS: Required<SocialPostSlots> = {
  eyebrow:       'NEW ARRIVAL',
  headline:      'Crafting your <em>copy…</em>',
  subhead:       'AI is generating headline, subhead, bullets, and CTA based on your product.',
  bullet1:       'Generating proof point…',
  bullet2:       'Generating proof point…',
  bullet3:       'Generating proof point…',
  cta:           'Shop now',
  handle:        '@yourbrand',
  badge_stars:   '★★★★★',
  badge_text:    '4.9 · 12,000 reviews',
  // Injected from the wizard's productName in effectiveSlots below.
  // This default only shows before a product is set.
  product_name:  'Product name',
  // Scaffolding fields — backend's suggest-copy fills these with short
  // product-context words for each generation. Defaults below only show
  // briefly during the shimmer (and as fallback if AI fails). Kept
  // category-neutral so they don't read as wrong on a non-skincare
  // product when the user lands on Step 6.
  brand_name:    'Brand',
  brand_tagline: 'tagline',
  issue_label:   'Detail',
  caption_left:  'Caption',
  caption_right: 'Label',
  // New JSON-template-friendly slots. AI fills body + bullet_list;
  // label_N are user-filled in the Tweaks panel.
  body:          'Generating body copy…',
  bullet_list:   'Generating benefit one\nGenerating benefit two\nGenerating benefit three',
  label_1:       '',
  label_2:       '',
  label_3:       '',
  label_4:       '',
  label_5:       '',
  label_6:       '',
  label_7:       '',
  label_8:       '',
};

// All 8 designs render at this reference size internally. Export
// pixelRatio = exportWidth / DESIGN_REFERENCE_SIZE so the downloaded
// PNG matches the platform's native dimensions (Step 2 picks the
// platform; this component reads its dimensions from props).
const DESIGN_REFERENCE_SIZE = 1080;

// Config for the Tweaks panel — one entry per SocialPostSlots field
// in the order they should appear. Filtered at render time: legacy
// templates show every entry; JSON templates show only the entries
// whose `key` is bound by at least one element via `slotKey`.
const TWEAK_FIELDS: Array<{
  key: keyof SocialPostSlots;
  label: string;
  multiline?: boolean;
  hint?: string;
}> = [
  { key: 'eyebrow', label: 'Eyebrow', hint: 'Small kicker above the headline' },
  { key: 'product_name', label: 'Product name', hint: 'Defaults to your product; edit to override' },
  { key: 'headline', label: 'Headline', multiline: true, hint: 'Wrap a key word in <em>…</em> for italic emphasis' },
  { key: 'subhead', label: 'Subheading', multiline: true, hint: 'Supporting sentence under the headline' },
  { key: 'body', label: 'Body text', multiline: true, hint: '1-2 sentence product paragraph' },
  { key: 'bullet_list', label: 'Bullet list', multiline: true, hint: 'One bullet per line — renders as a bulleted list' },
  { key: 'bullet1', label: 'Bullet 1', hint: 'Legacy single-bullet slot (use Bullet list for new templates)' },
  { key: 'bullet2', label: 'Bullet 2' },
  { key: 'bullet3', label: 'Bullet 3' },
  { key: 'cta', label: 'CTA', hint: 'Call-to-action label (e.g. "Shop now")' },
  { key: 'brand_name', label: 'Brand name', hint: 'Wordmark' },
  { key: 'brand_tagline', label: 'Brand tagline', hint: 'Small italic next to the wordmark' },
  { key: 'handle', label: 'Brand handle' },
  { key: 'badge_stars', label: 'Stars', hint: 'Review stars (e.g. ★★★★★)' },
  { key: 'badge_text', label: 'Stars label', hint: 'Text next to the stars (e.g. "4.9 · 2.3k reviews")' },
  { key: 'issue_label', label: 'Edition / Season', hint: 'e.g. "Édition Nº01" or "SS · 26"' },
  { key: 'caption_left', label: 'Caption — left' },
  { key: 'caption_right', label: 'Caption — right' },
  // Generic labels — only render in the Tweaks panel for JSON templates
  // that actually bind them (relevantSlotKeys filter handles this).
  { key: 'label_1', label: 'Label 1' },
  { key: 'label_2', label: 'Label 2' },
  { key: 'label_3', label: 'Label 3' },
  { key: 'label_4', label: 'Label 4' },
  { key: 'label_5', label: 'Label 5' },
  { key: 'label_6', label: 'Label 6' },
  { key: 'label_7', label: 'Label 7' },
  { key: 'label_8', label: 'Label 8' },
];

export default function StepSocialFinal({
  productFile,
  productImageUrl,
  productName,
  productDescription,
  designId,
  slots,
  onSlotsChange,
  exportWidth,
  exportHeight,
  onCostAdd,
  onCreditsChanged,
  hasHumanModel,
  transparentProductImage,
  brandLogoUrl,
  onBack,
  compact,
  onSaveAsRecreate,
}: StepSocialFinalProps) {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // AI-composed product image: gpt-image-1 takes the user's upload + a
  // Pillow-generated solid-cream rectangle and renders the product centered
  // on the cream bg with studio lighting + a soft shadow. Same pipeline as
  // /api/generate's product-shot path — bg is just a flat color instead
  // of a lifestyle scene. Until it resolves we render the raw upload.
  const [composedProductUrl, setComposedProductUrl] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const composeRef = useRef(false);
  const composedKeyRef = useRef<string | null>(null);

  // Scene-merge (composeMode='scene-merge'): the product is AI-merged into
  // the template's background scene; the merged image becomes the rendered
  // background and the JSON elements overlay on top.
  const [mergedSceneUrl, setMergedSceneUrl] = useState<string | null>(null);
  const sceneMergeRef = useRef(false);
  const sceneMergeKeyRef = useRef<string | null>(null);

  // Logo toggle — when the template has a `logo` slot AND the brand has a
  // logo, the editor lets the user remove it (default on). Off → pass no
  // logo URL so the slot renders empty.
  const [showLogo, setShowLogo] = useState(true);
  const hasLogoSlot = useMemo(() => {
    const tpl = getJsonTemplate(designId);
    if (!tpl) return false;
    const walk = (els: typeof tpl.elements): boolean =>
      els.some(
        (el) => el.type === 'logo-slot' || (el.type === 'stack' && walk(el.children)),
      );
    return walk(tpl.elements);
  }, [designId]);
  const effectiveLogoUrl = showLogo ? brandLogoUrl ?? null : null;

  // Effective slots — placeholder values are the base layer; whatever's
  // in parent state overlays them. This guarantees the 5 optional
  // brand/scaffolding fields ALWAYS render with a value even though
  // suggest-copy doesn't return them (and even though parent state
  // could be a partial AI-filled object after the first generation).
  const effectiveSlots: Required<SocialPostSlots> = {
    ...PLACEHOLDER_SLOTS,
    ...(slots ?? {}),
    // product_name defaults to the wizard's productName, but a manual
    // edit in the Tweaks panel (slots.product_name) takes precedence so
    // the user can override it. suggest-copy never returns product_name,
    // so after an AI fill this still falls back to productName.
    product_name:
      slots?.product_name?.trim() || productName?.trim() || PLACEHOLDER_SLOTS.product_name,
  };

  // For JSON templates, derive the set of slots the template actually
  // binds to via slotKey. The Tweaks panel filters by this set so a
  // template with only 2 text elements doesn't show 15 unrelated fields.
  // null = legacy hand-coded template (no schema introspection possible
  // → show every field, current behavior). Empty set = JSON template
  // that uses only staticText / no slot bindings → panel renders empty.
  const relevantSlotKeys: Set<keyof SocialPostSlots> | null = useMemo(() => {
    const tpl = getJsonTemplate(designId);
    if (!tpl) return null;
    const keys = new Set<keyof SocialPostSlots>();
    // Recurse — text/buttons can be nested inside `stack` (auto-layout)
    // elements, so a flat walk would miss every slot-bound field and the
    // Tweaks panel would wrongly show "static text only".
    const walk = (els: typeof tpl.elements) => {
      for (const el of els) {
        if ((el.type === 'text' || el.type === 'button') && el.slotKey) {
          keys.add(el.slotKey);
        }
        if (el.type === 'stack') walk(el.children);
      }
    };
    walk(tpl.elements);
    return keys;
  }, [designId]);

  // Collect label_N hints from the current JSON template — pass them
  // to suggest-copy so the AI fills user-defined Label slots with
  // product-adapted content matching the hint's intent (instead of
  // leaving them blank → falling back to staticText "NEW TEXT").
  // Each element's staticText is the hint; only elements with a hint
  // are included. Legacy templates return {} (no labels).
  const labelHints = useMemo<
    Partial<Record<`label_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`, string>>
  >(() => {
    const tpl = getJsonTemplate(designId);
    if (!tpl) return {};
    const hints: Record<string, string> = {};
    // Recurse into stacks — label-bound text can be nested in auto-layout.
    const walk = (els: typeof tpl.elements) => {
      for (const el of els) {
        if ((el.type === 'text' || el.type === 'button')
            && el.slotKey?.startsWith('label_')
            && el.staticText?.trim()) {
          hints[el.slotKey] = el.staticText.trim();
        }
        if (el.type === 'stack') walk(el.children);
      }
    };
    walk(tpl.elements);
    return hints as Partial<Record<`label_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`, string>>;
  }, [designId]);

  // ----- Auto-fire suggest-copy when productFile changes -----
  // Tracks which file we've already filled copy for. Per-file rather than
  // per-mount because /appv2's Section wrapper keeps StepSocialFinal
  // mounted across navigation (so form state survives Edit round-trips)
  // — a per-mount ref would only fill copy once ever, leaving the second
  // product showing placeholder text. The parent clears socialSlots on
  // upload (page.tsx handleUpload) so `slots` going from object → null
  // is the signal that a new product needs fresh copy.
  const filledFileRef = useRef<File | null>(null);

  const runCopyFill = useCallback(async () => {
    setError(null);
    setGenerating(true);
    try {
      const result = await suggestSocialCopy({
        productFile,
        productName,
        productDescription,
        labelHints,
      });
      onSlotsChange(result.slots);
      onCostAdd(result._cost ?? 0);
    } catch (err) {
      console.error('suggestSocialCopy failed:', err);
      setError(err instanceof Error ? err.message : 'Could not generate copy');
    } finally {
      setGenerating(false);
    }
  }, [productFile, productName, productDescription, labelHints, onSlotsChange, onCostAdd]);

  useEffect(() => {
    if (!productFile) return;
    if (filledFileRef.current === productFile) return;   // already filled this file
    if (slots) {
      // Parent already has slots for this file (e.g. user is returning
      // via back-nav and the slots survived). Record the file so we
      // don't re-fill unnecessarily.
      filledFileRef.current = productFile;
      return;
    }
    filledFileRef.current = productFile;
    void runCopyFill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFile, slots]);

  // ----- Compose product onto solid-color bg via gpt-image-1 -----
  // Replaces the prior bg-removal attempt. Sends [Pillow cream rectangle,
  // user product] to /api/generate with a strict "preserve product, fill
  // canvas, flat solid bg" prompt. Output composites seamlessly into the
  // design's slot — no visible studio-photo edge against the cream
  // template. Cost: Low quality ≈ $0.005, 1 credit. Cached by file +
  // designId + bg color so back-nav doesn't re-fire.
  useEffect(() => {
    if (!productFile) return;

    // SCENE-MERGE PATH: when the template is composeMode='scene-merge',
    // AI-composite the product INTO the template's background scene and
    // use that merged image as the rendered background (the product-slot
    // stays empty; text overlays). Fetches the scene image (data URL or
    // /social-templates path both work via fetch) and posts it + the
    // product to /api/compose-scene.
    const sceneTpl = getJsonTemplate(designId);
    if (sceneTpl?.composeMode === 'scene-merge') {
      const sceneEl = sceneTpl.elements.find(
        (e) => e.type === 'image' && e.id.endsWith('-background'),
      ) as { src: string } | undefined;
      const sceneKey = `${productFile.name}:${productFile.size}:${designId}:scene-merge:crop=${transparentProductImage?.length ?? 0}`;
      if (sceneMergeKeyRef.current === sceneKey && mergedSceneUrl) return;
      if (sceneMergeRef.current) return;
      if (!sceneEl) {
        // No background layer to merge into — fall through to the normal
        // paste path so the post still renders.
      } else {
        sceneMergeRef.current = true;
        sceneMergeKeyRef.current = sceneKey;
        setMergedSceneUrl(null);
        setComposing(true);
        // Use the product-slot rect (if any) as the placement mask so the
        // product fills exactly where the author drew the slot.
        const slotEl = sceneTpl.elements.find((e) => e.type === 'product-slot') as
          | { x: number; y: number; width: number; height: number }
          | undefined;
        const slot = slotEl
          ? { x: slotEl.x, y: slotEl.y, w: slotEl.width, h: slotEl.height }
          : undefined;
        // Compose the cropped product (from Step 2) if present, else raw.
        const sceneComposeFile = transparentProductImage
          ? dataUrlToFile(transparentProductImage, 'product.png')
          : productFile;
        (async () => {
          const sceneBlob = await (await fetch(sceneEl.src)).blob();
          const result = await composeScene({
            productFile: sceneComposeFile,
            sceneBlob,
            productName,
            productDescription,
            quality: 'low',
            slot,
            canvasSize: sceneTpl.canvas.width,
          });
          setMergedSceneUrl(result.imageUrl);
          onCostAdd(result.cost);
          onCreditsChanged?.();
        })()
          .catch((err) => {
            if (err instanceof InsufficientCreditsError) setError(err.message);
            else console.error('scene-merge failed:', err);
            sceneMergeKeyRef.current = null;
          })
          .finally(() => {
            setComposing(false);
            sceneMergeRef.current = false;
          });
        return;
      }
    }

    // PLAIN background path: OpenAI composes the (cropped) product onto a
    // flat slot color — the model extracts + relights it, so no rembg
    // cutout (which had jagged edges). Crop comes from Step 2; the cropped
    // image arrives as `transparentProductImage` (a normal RGB data URL,
    // despite the legacy prop name) and is used as the product to compose.
    // JSON template: read slotColor + canvas dims from its config.
    // Legacy template: fall back to the SOCIAL_DESIGN_* maps.
    const jsonTpl = getJsonTemplate(designId);
    // slotColor is ONLY the removable backdrop OpenAI composes the product
    // onto (so the lighting matches) — the backend then strips it to a
    // transparent PNG. The product floats in the slot over the template's
    // own background, so we don't send slot dimensions and the output is 1:1.
    const bgHex = jsonTpl
      ? jsonTpl.slotColor
      : SOCIAL_DESIGN_BG_COLOR[designId];
    const aspectRatio = jsonTpl ? '1:1' : SOCIAL_DESIGN_SLOT_ASPECT[designId];
    // The product we compose: the cropped image from Step 2 (if any),
    // else the raw upload.
    const composeFile = transparentProductImage
      ? dataUrlToFile(transparentProductImage, 'product.png')
      : productFile;
    // Cache key includes a crop discriminator (the cropped data URL
    // length) + hasHumanModel so a re-crop or flag flip recomposes.
    const cacheKey = `${productFile.name}:${productFile.size}:${designId}:${bgHex}:human=${hasHumanModel ? '1' : '0'}:crop=${transparentProductImage?.length ?? 0}`;
    if (composedKeyRef.current === cacheKey && composedProductUrl) return;
    if (composeRef.current) return;   // already in flight for this mount
    composeRef.current = true;
    composedKeyRef.current = cacheKey;
    // Cache miss — wipe the previous composed URL so the user doesn't
    // see the stale model (e.g. from the previous product) while the
    // new compose is in flight. displayProductUrl falls back to the
    // raw productImageUrl until the new compose returns.
    setComposedProductUrl(null);
    setComposing(true);
    generateImage({
      productFile: composeFile,
      templateId: `social-bg-${designId}`,
      frameNumber: 1,
      aspectRatio,
      productName,
      productDescription,
      quality: 'low',
      bgHex,
      hasHumanModel: hasHumanModel === true,
    })
      .then((result) => {
        setComposedProductUrl(result.imageUrl);
        onCostAdd(result.cost);
        onCreditsChanged?.();
      })
      .catch((err) => {
        // InsufficientCredits is the one error worth surfacing — the user
        // can take action (upgrade). For transient/timeout/safety errors
        // we silently fall back to the raw upload so the post still
        // renders rather than blocking the whole flow on a retryable
        // network blip.
        if (err instanceof InsufficientCreditsError) {
          setError(err.message);
        } else {
          console.error('compose product failed (falling back to raw upload):', err);
        }
        // On error, also clear the cache key so a retry isn't treated as
        // a cache hit on the failed key.
        composedKeyRef.current = null;
      })
      .finally(() => {
        setComposing(false);
        // CRITICAL: reset the in-flight lock on BOTH success and failure.
        // In /appv2 the Section wrapper keeps children mounted across
        // navigation (so form state survives an Edit round-trip), so
        // StepSocialFinal doesn't unmount when the user goes back to
        // Step 3 to swap products. Without this reset, composeRef stays
        // true after the first successful compose and silently blocks
        // every subsequent productFile change — the cache "doesn't clear"
        // because no new compose request ever fires.
        composeRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFile, designId, hasHumanModel, transparentProductImage]);

  // The product image we actually render: AI-composed > raw upload.
  const displayProductUrl = composedProductUrl || productImageUrl;

  // ----- Render preview at fixed 1080×1080, scaled to fit container -----
  // Approach: an invisible "export" post lives at 1080×1080 so html-to-image
  // captures it at full resolution. A visible "display" post is the same
  // markup wrapped in a CSS-transform-scale container that fits available
  // width.
  const exportRef = useRef<HTMLDivElement>(null);
  const displayWrapRef = useRef<HTMLDivElement>(null);
  const [displayScale, setDisplayScale] = useState(0.5);

  useEffect(() => {
    if (!displayWrapRef.current) return;
    const wrap = displayWrapRef.current;
    const ro = new ResizeObserver(() => {
      // Scale to fit BOTH width and height — the previous version only
      // accounted for width, so the 1080×1080 design overflowed vertically
      // in narrow-but-tall containers (the user sees the top half only).
      const wScale = (wrap.clientWidth  - 8) / 1080;
      const hScale = (wrap.clientHeight - 8) / 1080;
      const next = Math.max(0.05, Math.min(1, wScale, hScale));
      setDisplayScale(next);
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  // ----- Tweaks: shallow-update one slot at a time -----
  const updateSlot = (key: keyof SocialPostSlots, value: string) => {
    onSlotsChange({ ...effectiveSlots, [key]: value });
  };

  // ----- Download as PNG -----
  const handleDownload = async () => {
    if (!exportRef.current) return;
    setDownloading(true);
    try {
      // Export node renders at DESIGN_REFERENCE_SIZE (1080). pixelRatio
      // scales the captured pixels so the user gets the picked platform
      // size — 1080→1080 = 1.0, 1080→1200 = 1.11, 1080→640 = 0.59.
      // Fonts are now self-hosted (see src/fonts.ts) — same-origin so
      // html-to-image inlines them as base64 in the captured SVG. This
      // replaces the prior skipFonts:true workaround for Google Fonts
      // CORS, which was unreliable: the SVG decode context couldn't
      // always access cross-origin web fonts, causing headlines to wrap
      // to extra lines in the downloaded PNG while the preview rendered
      // fine. document.fonts.ready ensures every weight/style is loaded
      // before capture.
      await document.fonts.ready;
      const ratio = exportWidth / DESIGN_REFERENCE_SIZE;
      const dataUrl = await toPng(exportRef.current, {
        pixelRatio: ratio,
        cacheBust: true,
        // Matches the active design's canvas color so any transparent edge
        // in the export blends seamlessly. Falls back to Editorial cream.
        backgroundColor: (SOCIAL_DESIGN_POST_BG_COLOR[designId] ?? getJsonTemplate(designId)?.canvas.backgroundColor ?? '#efe7d8'),
      });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `socialpost-${designId}-${exportWidth}x${exportHeight}-${Date.now()}.png`;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // ----- Save as Recreate template (dev) -----
  // Same capture path as handleDownload — html-to-image on the design's
  // export DOM node — but instead of triggering a download we hand the
  // base64 PNG to the parent so it can POST to /api/recreate-templates-save.
  const [recreateSaveStatus, setRecreateSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const handleSaveAsRecreate = async () => {
    if (!onSaveAsRecreate) return;
    if (recreateSaveStatus === 'saving') return;
    if (!exportRef.current) return;
    setRecreateSaveStatus('saving');
    try {
      await document.fonts.ready;
      const ratio = exportWidth / DESIGN_REFERENCE_SIZE;
      const dataUrl = await toPng(exportRef.current, {
        pixelRatio: ratio,
        cacheBust: true,
        backgroundColor: (SOCIAL_DESIGN_POST_BG_COLOR[designId] ?? getJsonTemplate(designId)?.canvas.backgroundColor ?? '#efe7d8'),
      });
      await onSaveAsRecreate(dataUrl);
      setRecreateSaveStatus('saved');
      setTimeout(() => setRecreateSaveStatus('idle'), 4000);
    } catch (err) {
      console.error('Save as Recreate failed:', err);
      setRecreateSaveStatus('error');
      setTimeout(() => setRecreateSaveStatus('idle'), 4000);
    }
  };

  // ----- Save to Gallery -----
  const handleSaveToGallery = async () => {
    if (!exportRef.current || saveStatus === 'saving') return;
    setSaveStatus('saving');
    try {
      await document.fonts.ready;
      const ratio = exportWidth / DESIGN_REFERENCE_SIZE;
      const dataUrl = await toPng(exportRef.current, {
        pixelRatio: ratio,
        cacheBust: true,
        backgroundColor: (SOCIAL_DESIGN_POST_BG_COLOR[designId] ?? getJsonTemplate(designId)?.canvas.backgroundColor ?? '#efe7d8'),
      });
      // Convert data URL → Blob for savePost.
      const blob = await (await fetch(dataUrl)).blob();
      await savePost({
        blob,
        templateId: `social-${designId}`,
        aspectRatio: '1:1',
        headline: effectiveSlots.headline.replace(/<[^>]+>/g, ''),  // strip <em>
        tagline: effectiveSlots.subhead,
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err) {
      console.error('Save to Gallery failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 4000);
    }
  };

  // ----- Pick the design component -----
  // All 4 designs share the same SocialPostSlots shape and productImageUrl
  // prop, so dispatch is just a switch by `designId`. Each component owns
  // its own 1080×1080 layout + co-located CSS namespaced under its root
  // class. forExport is reserved for future per-design export tweaks.
  const renderDesign = (forExport: boolean) => {
    void forExport;
    const designProps = { slots: effectiveSlots, productImageUrl: displayProductUrl };

    // JSON registry check FIRST — these template ids are loaded from
    // social-designs/json-templates/*.json at build time. Authoring a
    // new template = drop a JSON file there + restart Vite (or HMR).
    // No new TSX case needed here.
    const jsonTpl = getJsonTemplate(designId);
    if (jsonTpl) {
      // For scene-merge templates, pass the AI-merged scene so the
      // renderer uses it as the background and skips the original
      // background layer + the product-slot paste.
      return (
        <JsonSocialPost
          config={jsonTpl}
          {...designProps}
          mergedSceneUrl={jsonTpl.composeMode === 'scene-merge' ? mergedSceneUrl : null}
          brandLogoUrl={effectiveLogoUrl}
        />
      );
    }

    // Legacy hand-coded templates fall through to the switch.
    switch (designId) {
      case 'editorial': return <PostEditorial {...designProps} />;
      case 'blush':     return <PostBlush     {...designProps} />;
      case 'botanical': return <PostBotanical {...designProps} />;
      case 'mono':      return <PostMono      {...designProps} />;
      case 'noir':      return <PostNoir      {...designProps} />;
      case 'pop':       return <PostPop       {...designProps} />;
      case 'vintage':   return <PostVintage   {...designProps} />;
      case 'street':    return <PostStreet    {...designProps} />;
      default:
        return (
          <div style={{ width: 1080, height: 1080, background: '#ddd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p>Unknown design "{designId}"</p>
          </div>
        );
    }
  };

  return (
    <div className={
      compact
        ? 'h-[calc(100vh-160px)] min-h-[520px] flex flex-col'
        : 'h-full flex flex-col'
    }>
      {!compact && (
        <StepHeader
          icon="ri-magic-line"
          title="Your Designed Post"
          subtitle={
            generating && composing
              ? 'AI is writing your post copy and composing your product image…'
              : generating
              ? 'AI is writing your post copy…'
              : composing
              ? 'AI is composing your product image — this takes about 20 seconds…'
              : 'Edit any field on the right. Download as PNG or save to your gallery when ready.'
          }
        />
      )}

      {/* Hidden export node — full 1080×1080 layout. Previous version used
          `position: fixed; top: -99999px` which produced blank PNGs from
          html-to-image (off-screen elements don't always serialize properly
          through SVG foreignObject). Pattern below keeps the node in
          document flow with valid layout, but visually clipped to 0×0 via
          the outer wrapper's overflow:hidden. html-to-image captures the
          inner node's actual computed styles correctly. */}
      <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }} aria-hidden="true">
        <div
          ref={exportRef}
          style={{
            width: 1080,
            height: 1080,
            position: 'relative',
          }}
        >
          {renderDesign(true)}
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4 overflow-hidden">
        {/* LEFT: scaled preview */}
        <div className="min-h-0 flex flex-col gap-3 rounded-2xl bg-white border-2 border-white shadow-lg shadow-primary/15 p-6">
          <div ref={displayWrapRef} className="flex-1 min-h-0 flex items-center justify-center overflow-hidden relative">
            {/* Inner wrapper sized to the scaled box. transform: scale on the
                1080×1080 post keeps text + grid mathematically perfect. */}
            <div
              style={{
                width: 1080 * displayScale,
                height: 1080 * displayScale,
                position: 'relative',
              }}
            >
              <div
                style={{
                  transform: `scale(${displayScale})`,
                  transformOrigin: 'top left',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              >
                {renderDesign(false)}
              </div>

              {/* Shimmer skeleton overlay — covers the design while AI is
                  filling copy slots OR composing the product image. Once
                  both finish, the overlay unmounts and reveals the final
                  post. Without this overlay, the live field-by-field
                  update is too subtle to notice. */}
              {(generating || composing) && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 6,
                    overflow: 'hidden',
                    // Base tint matches the active design's canvas color so
                    // the shimmer feels like the design is "developing" in
                    // place rather than under a generic white loader.
                    background: (SOCIAL_DESIGN_POST_BG_COLOR[designId] ?? getJsonTemplate(designId)?.canvas.backgroundColor ?? '#efe7d8'),
                  }}
                  aria-label="Loading"
                >
                  {/* Skeleton bones — mimic PostEditorial's layout so the
                      transition to the real design doesn't feel jarring. */}
                  <div
                    style={{
                      transform: `scale(${displayScale})`,
                      transformOrigin: 'top left',
                      width: 1080,
                      height: 1080,
                      padding: 72,
                      display: 'grid',
                      gridTemplateColumns: '1.05fr 0.95fr',
                      gap: 48,
                      boxSizing: 'border-box',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                      <div style={{ width: 140, height: 18, borderRadius: 4, background: 'rgba(0,0,0,0.08)' }} />
                      <div style={{ width: '90%', height: 110, borderRadius: 6, background: 'rgba(0,0,0,0.08)' }} />
                      <div style={{ width: '70%', height: 110, borderRadius: 6, background: 'rgba(0,0,0,0.08)' }} />
                      <div style={{ width: '85%', height: 50, borderRadius: 4, background: 'rgba(0,0,0,0.05)', marginTop: 12 }} />
                      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{ width: '60%', height: 22, borderRadius: 4, background: 'rgba(0,0,0,0.06)' }} />
                        <div style={{ width: '50%', height: 22, borderRadius: 4, background: 'rgba(0,0,0,0.06)' }} />
                        <div style={{ width: '45%', height: 22, borderRadius: 4, background: 'rgba(0,0,0,0.06)' }} />
                      </div>
                    </div>
                    <div style={{ background: '#e5dccb', borderRadius: 6 }} />
                  </div>

                  {/* The actual shimmer sweep — a translucent diagonal band
                      moving across the whole panel. Uses an inline keyframes
                      style so the animation isn't reliant on any external
                      CSS being loaded. */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
                      backgroundSize: '200% 100%',
                      animation: 'social-shimmer 1.6s linear infinite',
                    }}
                  />
                  <style>{`
                    @keyframes social-shimmer {
                      0%   { background-position: 200% 0; }
                      100% { background-position: -200% 0; }
                    }
                  `}</style>

                  {/* Center loading caption — overlaid above the shimmer
                      band via z-index so it stays legible even mid-sweep. */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12,
                      pointerEvents: 'none',
                      color: '#5c4d3e',
                    }}
                  >
                    <i className="ri-sparkling-2-fill" style={{ fontSize: 32, color: '#8a6a3a' }} />
                    <p style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontSize: 22, margin: 0 }}>
                      {generating && composing
                        ? 'Crafting your copy + composing your product…'
                        : composing
                        ? 'Composing your product onto the canvas…'
                        : 'Crafting your copy…'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-dark-muted text-center">
            Live preview · 1080 × 1080 · scaled {(displayScale * 100).toFixed(0)}%
          </p>
        </div>

        {/* RIGHT: tweaks panel */}
        <div className="min-h-0 flex flex-col rounded-2xl bg-white border-2 border-white shadow-lg shadow-primary/15">
          <div className="flex-shrink-0 px-5 pt-5 pb-3">
            <h3 className="font-display font-bold text-dark text-base">Tweaks</h3>
            <p className="text-xs text-dark-muted mt-0.5">
              Edit any field — changes appear live in the preview.
            </p>
            {error && (
              <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
                <i className="ri-error-warning-line text-sm flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">{error}</span>
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-3 space-y-3">
            {TWEAK_FIELDS
              .filter((f) => {
                // Legacy hand-coded templates (relevantSlotKeys === null)
                // never use generic label_N slots — those are a JSON-template-
                // only feature. Always hide them for legacy to keep the
                // Tweaks panel focused on what the template actually renders.
                if (relevantSlotKeys === null) {
                  return !f.key.startsWith('label_');
                }
                // JSON templates: show only slots the template binds.
                return relevantSlotKeys.has(f.key);
              })
              .map((f) => (
                <TweakField
                  key={f.key}
                  label={f.label}
                  value={effectiveSlots[f.key]}
                  onChange={(v) => updateSlot(f.key, v)}
                  multiline={f.multiline}
                  hint={f.hint}
                />
              ))}

            {/* Logo toggle — only when the template has a `logo` slot AND
                the brand has a logo. Lets the user drop the logo if they
                don't want it on this post. */}
            {hasLogoSlot && brandLogoUrl && (
              <label className="flex items-center gap-2 mt-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showLogo}
                  onChange={(e) => setShowLogo(e.target.checked)}
                  className="accent-primary"
                />
                <span className="text-sm text-dark">Show brand logo</span>
              </label>
            )}

            {/* Empty-state hint for JSON templates with no slot bindings.
                The user authored a template using only staticText — there
                are no AI-fillable fields to tweak. */}
            {relevantSlotKeys !== null && relevantSlotKeys.size === 0 && (
              <p className="text-xs text-dark-muted leading-relaxed italic">
                This template uses static text only — there are no AI-filled
                fields to edit. Adjust copy by editing the template in the
                builder.
              </p>
            )}

            <button
              onClick={runCopyFill}
              disabled={generating}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-surface-dark hover:border-primary/40 text-dark-muted hover:text-dark text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className={`text-base ${generating ? 'ri-loader-4-line animate-spin' : 'ri-magic-line'}`} />
              {generating ? 'Generating…' : 'Regenerate with AI'}
            </button>
          </div>
        </div>
      </div>

      {/* Footer panel — Back / Save / Download — matches the steps 1-5 + step 7/8 footer style */}
      <div className="flex-shrink-0 mt-4 bg-surface-panel rounded-2xl border-2 border-white px-6 py-4 shadow-lg shadow-primary/15 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-dark hover:bg-surface transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          Back
        </button>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Dev-only: "Save as Recreate template" — only renders when the
              parent (currently /appv2) passes onSaveAsRecreate. Dashed
              border + DEV pill signal authoring tooling. */}
          {onSaveAsRecreate && (
            <button
              onClick={handleSaveAsRecreate}
              disabled={recreateSaveStatus === 'saving' || generating || composing}
              title="DEV: capture this composition as a reusable Recreate template"
              className={`inline-flex items-center gap-2 px-4 py-3 rounded-full border-2 border-dashed text-xs font-semibold transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
                recreateSaveStatus === 'saved'
                  ? 'border-emerald-500 text-emerald-700 bg-emerald-50'
                  : recreateSaveStatus === 'error'
                  ? 'border-red-400 text-red-600 bg-red-50'
                  : 'border-fuchsia-400/60 text-fuchsia-700 hover:bg-fuchsia-50'
              }`}
            >
              <span className="hidden sm:inline-block px-1.5 py-0.5 rounded bg-fuchsia-200 text-fuchsia-900 text-[9px] font-black uppercase tracking-wider">
                Dev
              </span>
              {recreateSaveStatus === 'saving' && <><i className="ri-loader-4-line animate-spin text-base" />Capturing…</>}
              {recreateSaveStatus === 'saved' && <><i className="ri-check-line text-base" />Saved as Recreate</>}
              {recreateSaveStatus === 'error' && <><i className="ri-error-warning-line text-base" />Retry</>}
              {recreateSaveStatus === 'idle' && <><i className="ri-magic-line text-base" />Save as Recreate</>}
            </button>
          )}
          <button
            onClick={handleSaveToGallery}
            disabled={saveStatus === 'saving' || generating || composing}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-surface-dark hover:border-primary/30 text-dark hover:text-primary text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {saveStatus === 'idle' && (<><i className="ri-heart-3-line text-base" />Save to My Creatives</>)}
            {saveStatus === 'saving' && (<><i className="ri-loader-4-line text-base animate-spin" />Saving…</>)}
            {saveStatus === 'saved' && (<><i className="ri-check-line text-base text-green-600" />Saved</>)}
            {saveStatus === 'error' && (<><i className="ri-error-warning-line text-base text-red-500" />Try again</>)}
          </button>

          <button
            onClick={handleDownload}
            disabled={downloading || generating || composing}
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-base font-bold transition-colors cursor-pointer shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <i className={`text-lg ${downloading ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'}`} />
            {downloading ? 'Rendering…' : `Download ${exportWidth}×${exportHeight}`}
          </button>

          <button
            onClick={() => navigate('/gallery')}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-surface-dark hover:border-primary/30 text-dark hover:text-primary text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            title="Go to your saved gallery"
          >
            My Creatives
            <i className="ri-arrow-right-line text-base" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ----- Inline tweak field component -----
// Keeps StepSocialFinal readable. Single-line by default; pass `multiline`
// to render a textarea. Hint goes below the input as muted helper text.
interface TweakFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  hint?: string;
}

function TweakField({ label, value, onChange, multiline, hint }: TweakFieldProps) {
  const baseClass = 'w-full px-3 py-2 rounded-lg border border-dark-muted/40 bg-white focus:border-primary focus:outline-none text-sm text-dark transition-colors';
  return (
    <div>
      <label className="block text-xs font-bold text-dark-muted uppercase tracking-wide mb-1.5">
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className={baseClass + ' resize-y leading-snug'}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={baseClass}
        />
      )}
      {hint && <p className="text-xs text-dark-muted mt-1 leading-tight">{hint}</p>}
    </div>
  );
}

